import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import MindMapWrapper from "@core/mindmap";
import { Text } from "@core/text";
import { Button } from "@core/button";
import {
  getMindmapGenerator,
  useGetMindmapGenerator,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import logo from "@packages/assets/logo.png";
import ErrorComponent from "@features/tools/components/tool_components/ErrorComponent";
import HeroTitle from "@features/tools/components/tool_components/HeroTitle";
import {
  ArrowLeft,
  BookOpen,
  GraduationCap,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { CourseData } from "@packages/constant/CourseData";

export default function MindMapView() {
  const { id } = useParams();
  const mindMapId = Number(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState(false);
  const [generatingToolId, setGeneratingToolId] = useState<{
    id: number;
    type: string;
  } | null>(null);

  const hasProcessingNode = (nodes: any[]): boolean => {
    return nodes.some((node) => {
      if (node.toolInfo?.status === "PROCESSING") {
        return true;
      }
      return node.children?.length && hasProcessingNode(node.children);
    });
  };
  const findGeneratingToolNode = (nodes: any[]): any => {
    for (const node of nodes) {
      if (
        node.toolInfo?.status !== "PROCESSING" &&
        node.toolInfo?.toolType === generatingToolId?.type &&
        node.toolInfo?.id === generatingToolId?.id
      ) {
        return node;
      }

      if (node.children?.length) {
        const found = findGeneratingToolNode(node.children);
        if (found) return found;
      }
    }

    return null;
  };

  const {
    data: mindMapData,
    isLoading: isLoadingMindMap,
    error: errorMindMap,
    refetch: refetchMindMap,
  } = useGetMindmapGenerator(mindMapId, {
    query: {
      enabled: !!mindMapId,
      refetchInterval: (query) => {
        if (query?.state?.data?.status === "FAILED") {
          navigate("/content/mindmaps");
          return false;
        }
        if (
          !query?.state?.data?.nodes ||
          query?.state?.data?.nodes.length === 0
        ) {
          return 5000;
        }
        if (!query?.state?.data?.imageUrl) {
          return 3000;
        }
        if (hasProcessingNode(query?.state?.data?.nodes)) return 3000;

        return false;
      },
      queryFn: async () => {
        const response = await getMindmapGenerator(mindMapId);
        return response;
      },
    },
  });
  // when generatingToolid node in the nodes goes from processing to READY or Failed set the generatingToolId to null
  useEffect(() => {
    if (mindMapData?.nodes) {
      //traverse allll the nodes and find the tool that is not processing and has the same type as the generatingToolId nodes could have children
      const tool = findGeneratingToolNode(mindMapData.nodes);

      console.log(
        "NODES DATA CHANGED",
        tool,
        "GENERATING TOOL ID",
        generatingToolId,
      );
      if (tool) {
        setGeneratingToolId(null);
      }
    }
  }, [mindMapData?.nodes]);

  const toggleFullscreenModal = () => {
    setIsFullscreenModalOpen(!isFullscreenModalOpen);
  };

  const renderMindMapContent = () => {
    if (!mindMapData?.nodes || mindMapData.nodes.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500 mb-4"></div>
          <Text variant="h3" className="text-center mb-2">
            {t("mindmap.generatingMindMap")}
          </Text>
          <Text className="text-center text-gray-500">
            {t("mindmap.pleaseWait")}
          </Text>
        </div>
      );
    }

    return (
      <MindMapWrapper
        props={{
          mindmapId: mindMapId,
          data: mindMapData.nodes,
          imageUrl: mindMapData.imageUrl,
          generatingToolId: generatingToolId,
          setGeneratingToolId: setGeneratingToolId,
        }}
      />
    );
  };

  if (isLoadingMindMap && !mindMapData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
        <img
          src={logo}
          alt="logo"
          className="absolute w-8 h-8 animate-bounce mt-2"
        />
      </div>
    );
  }

  if (errorMindMap) {
    return (
      <ErrorComponent
        title={t("mindmap.error.fetchTitle")}
        refetch={refetchMindMap}
        description={t("mindmap.error.fetchDescription")}
        retryButton={t("mindmap.error.retryButton")}
      />
    );
  }

  return (
    <>
      <div className="min-h-screen px-4 py-4 bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-900 dark:to-blue-900">
        <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-md rounded-xl p-4 sm:p-8 shadow-lg border border-white/20 dark:border-gray-700/20 relative w-full mx-auto h-full">
          <div className="flex justify-between items-center mb-4">
            <Button
              onClick={() => navigate("/content/mindmaps")}
              bgColor="bg-transparent hover:bg-transparent"
              textColor="text-gray-800 dark:text-gray-200"
              size="md"
              variant="ghost"
              rounded="lg"
              className="flex items-center px-4 py-2 w-auto justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back")}
            </Button>

            <Button
              onClick={toggleFullscreenModal}
              bgColor="bg-blue-600 hover:bg-blue-700"
              textColor="text-white"
              size="md"
              rounded="lg"
              className="flex items-center px-4 py-2"
            >
              <Maximize2 className="h-4 w-4 mr-2" />
              {t("common.fullscreen") || "Fullscreen"}
            </Button>
          </div>

          <div className="animate-fadeIn relative">
            <div className="items-center justify-center mx-auto">
              <HeroTitle
                title={mindMapData?.title || ""}
                titleStyle="bg-gradient-to-r from-cyan-600 via-blue-600 to-rose-600 dark:from-cyan-400 dark:via-blue-400 dark:to-rose-400 !text-2xl"
                titleBackgroundStyle="bg-gradient-to-r from-cyan-50/30 to-blue-50/30 dark:from-cyan-900/30 dark:to-blue-900/30"
                buttonLink="/content/mindmaps"
                buttonStyle="bg-blue-600 hover:bg-blue-700"
              />
            </div>

            <div id="mindmap-display-area" className="relative w-full">
              <div className="w-full h-[70vh]">{renderMindMapContent()}</div>
              {/* course and grade level */}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
              {mindMapData?.courseName && (
                <div
                  className={`flex items-start bg-blue-500/90 px-3 py-1.5 rounded-full max-w-full`}
                >
                  <BookOpen size={14} className="text-white shrink-0 mt-1" />
                  <Text
                    variant="span"
                    size={"sm"}
                    weight={"medium"}
                    className="text-white ml-2 break-words"
                  >
                    {`courseType.${CourseData[
                      mindMapData.courseName as keyof typeof CourseData
                    ]?.name(mindMapData.gradeLevel ?? 8)}`}
                  </Text>
                </div>
              )}
              {mindMapData?.gradeLevel !== null &&
                mindMapData?.gradeLevel !== undefined &&
                (Number(mindMapData.gradeLevel) !== 0 ||
                  Number(mindMapData.gradeLevel) !== -1) && (
                  <div
                    className={`flex items-start bg-purple-500/90 px-3 py-1.5 rounded-full max-w-full`}
                  >
                    <GraduationCap
                      size={14}
                      className="text-white shrink-0 mt-1"
                    />
                    <Text
                      variant="span"
                      size={"sm"}
                      weight={"medium"}
                      className="text-white ml-2 break-words"
                    >
                      {t("hooks.form.gradeOf", {
                        grade: mindMapData.gradeLevel,
                      })}
                    </Text>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreenModalOpen && (
        <div className="fixed inset-0 bg-black z-[9999] flex flex-col overflow-hidden">
          <div className="p-4 flex justify-end items-center">
            <Button
              onClick={toggleFullscreenModal}
              bgColor="bg-transparent hover:bg-white/20"
              textColor="text-white"
              size="md"
              rounded="full"
              className="flex items-center p-3"
            >
              <Minimize2 className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 p-2">{renderMindMapContent()}</div>
        </div>
      )}
    </>
  );
}
